// src/components/your-path/CreateMultipleStoreRolls.js
import React, { useState } from "react"
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Divider,
  Typography,
  Card,
  Row, // Import Row
  Col, // Import Col
} from "antd"
import { PlusOutlined, MinusCircleOutlined, AppstoreAddOutlined } from "@ant-design/icons"
import { collection, doc, serverTimestamp, writeBatch } from "firebase/firestore"
import { db } from "../../../firebase/config" // Adjust path

const { Text } = Typography

export default function CreateMultipleStoreRolls({ materialData, materialLoading }) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedMaterialForBatch, setSelectedMaterialForBatch] = useState(null)
  const [form] = Form.useForm()

  const showDrawer = () => {
    setOpen(true)
    form.setFieldsValue({
      rolls: [
        {
          // Start with one empty roll entry
          barcode: undefined,
          remainingMeters: undefined,
          rollWidth: undefined,
          weight: undefined,
          price: undefined,
          pallet: "",
          comment: "",
        },
      ],
    })
  }

  const onClose = () => {
    setOpen(false)
    form.resetFields()
    setSelectedMaterialForBatch(null)
  }

  const handleMaterialChangeForBatch = (value, optionObject) => {
    setSelectedMaterialForBatch(optionObject)
  }

  const onFinish = async (values) => {
    if (!selectedMaterialForBatch) {
      message.error("Please select a material for the batch.")
      return
    }
    if (!values.rolls || values.rolls.length === 0) {
      message.error("Please add at least one roll.")
      return
    }

    setSubmitting(true)

    const {
      rolls: _selectedMaterialRolls,
      history,
      ...intermediateMaterial
    } = selectedMaterialForBatch
    const materialToStore = { ...intermediateMaterial }
    if (Object.prototype.hasOwnProperty.call(materialToStore, "")) {
      delete materialToStore[""]
    }

    const batch = writeBatch(db)
    const rollStoreCollectionRef = collection(db, "rollStore")

    values.rolls.forEach((rollEntry) => {
      const rollDataForDb = {
        barcode: rollEntry.barcode,
        remainingMeters: rollEntry.remainingMeters,
        rollWidth: rollEntry.rollWidth,
        weight: rollEntry.weight,
        price: rollEntry.price,
        pallet: rollEntry.pallet || "",
        comment: rollEntry.comment || "",
        inUse: false,
        shipping: false,
        material: materialToStore,
        materialId: selectedMaterialForBatch.id,
        status: "Stock",
        originalMeters: rollEntry.remainingMeters,
        rollLocation: 0,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        inOrder: false,
        verified: false,
      }
      const newRollRef = doc(rollStoreCollectionRef)
      batch.set(newRollRef, rollDataForDb)
    })

    try {
      await batch.commit()
      message.success(`Successfully added ${values.rolls.length} rolls!`)
      onClose()
    } catch (error) {
      console.error("Error adding multiple rolls:", error)
      message.error("Failed to add rolls. Please check console for details.")
    } finally {
      setSubmitting(false)
    }
  }

  const onFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo)
    message.error("Please fill in all required fields for each roll correctly.")
  }

  return (
    <>
      <Button icon={<AppstoreAddOutlined />} onClick={showDrawer} style={{ marginLeft: 8 }}>
        Add Multiple Rolls
      </Button>
      <Drawer
        title="Add Multiple Rolls to Store"
        placement="right"
        onClose={onClose}
        open={open}
        width={1200}
        destroyOnClose
        footer={
          <Space style={{ textAlign: "right", width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={submitting}>
              Add All Rolls
            </Button>
          </Space>
        }>
        <Form
          form={form}
          name="createMultipleRollsForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          style={{ width: "100%" }}
          autoComplete="off">
          <Form.Item
            label="Material for Batch"
            name="batchMaterialId"
            rules={[{ required: true, message: "Please select a material for the batch." }]}>
            <Select
              placeholder="Select or search for material"
              onChange={handleMaterialChangeForBatch}
              showSearch
              fieldNames={{ label: "material", value: "id" }}
              loading={materialLoading}
              filterOption={(input, option) =>
                (option?.material ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={materialData || []}
            />
          </Form.Item>

          <Divider>Rolls to Add</Divider>

          <Form.List name="rolls">
            {(
              fields,
              { add, remove },
              { errors } // Added { errors } for Form.List level errors
            ) => (
              <>
                {/* Scrollable area for roll entries */}
                <div
                  style={{
                    maxHeight: "calc(100vh - 400px)",
                    overflowY: "auto",
                    padding: "0 10px 10px 0",
                  }}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card
                      key={key}
                      style={{
                        marginBottom: 16,
                        backgroundColor: "#f5f5f5",
                      }}
                      title={<Text strong>Roll {index + 1}</Text>}
                      extra={
                        fields.length > 1 ? (
                          <MinusCircleOutlined
                            style={{ color: "red", fontSize: "18px", cursor: "pointer" }}
                            onClick={() => remove(name)}
                          />
                        ) : null
                      }>
                      {/* Row 1: Barcode, Meters, Width */}
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "barcode"]}
                            label="Barcode"
                            rules={[{ required: true, message: "Barcode!" }]}>
                            <InputNumber style={{ width: "100%" }} placeholder="Barcode" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "remainingMeters"]}
                            label="Meters"
                            rules={[
                              { required: true, message: "Meters!" },
                              { type: "number", min: 0 },
                            ]}>
                            <InputNumber style={{ width: "100%" }} placeholder="Meters" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "rollWidth"]}
                            label="Width (mm)"
                            rules={[
                              { required: true, message: "Width!" },
                              { type: "number", min: 0 },
                            ]}>
                            <InputNumber style={{ width: "100%" }} placeholder="Width" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "weight"]}
                            label="Weight (kg)"
                            rules={[
                              { required: true, message: "Weight!" },
                              { type: "number", min: 0 },
                            ]}>
                            <InputNumber style={{ width: "100%" }} placeholder="Weight" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "price"]}
                            label="Price (€)"
                            rules={[
                              { required: true, message: "Price!" },
                              { type: "number", min: 0 },
                            ]}>
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="Price"
                              formatter={(value) =>
                                `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              }
                              parser={(value) => String(value).replace(/€\s?|(,*)/g, "")}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Row 2: Pallet, Comment */}
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "pallet"]}
                            label="Pallet (Optional)">
                            <Input placeholder="Pallet ID or location" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "comment"]}
                            label="Comment (Optional)">
                            <Input.TextArea rows={1} placeholder="Roll specific comment" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>

                {/* "Add Another Roll" button moved outside the scrollable div */}
                <Form.Item style={{ marginTop: "16px" }}>
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        barcode: undefined,
                        remainingMeters: undefined,
                        rollWidth: undefined,
                        weight: undefined,
                        price: undefined,
                        pallet: "",
                        comment: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}>
                    Add Another Roll Entry
                  </Button>
                  <Form.ErrorList errors={errors} />{" "}
                  {/* Display Form.List level errors, like min/max count */}
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </>
  )
}
